import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

import { ICreateAuditParams } from '../../interfaces/audits/create-audit.interface';
import { IEditDocChangesConfirmConfig } from '../../interfaces/edit-doc-changes-confirm-modal.interface';
import { CheckForAuditsService } from '../../services/audits/check-for-audits/check-for-audits.service';
import { CreateAuditService } from '../../services/audits/create-audit/create-audit.service';
import { ModalDialogService } from '../../services/modalDialog/modal-dialog.service';
import { ChangesReviewTableComponent } from '../audits-review/changes-review-table/changes-review-table.component';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { StyledButtonComponent } from '../styled-button/styled-button.component';

/* Component intended to use with modal */
@Component({
  selector: 'app-edit-doc-changes-confirm',
  templateUrl: './edit-doc-changes-confirm.component.html',
  styleUrls: ['./edit-doc-changes-confirm.component.scss'],
  imports: [
    ProgressBarComponent,
    ChangesReviewTableComponent,
    ReactiveFormsModule,
    FormsModule,
    StyledButtonComponent
],
})
export class EditDocChangesConfirmComponent implements OnInit, OnDestroy {
  @Input() configData: IEditDocChangesConfirmConfig;
  readonly auditPendingMessage = 'Edit Doc Audit Pending';
  readonly auditCreatedMessage = 'Edit Doc Audit Created';
  private _onDestroy = new Subject<void>();
  awaitingAuditsCall = false;
  auditPending = false;
  auditNote = '';
  modalResponse = new BehaviorSubject<
    typeof this.auditPendingMessage | typeof this.auditCreatedMessage | void
  >(undefined);

  constructor(
    private checkForAudits: CheckForAuditsService,
    private createAuditService: CreateAuditService,
    private modalService: ModalDialogService,
  ) {}

  /* checkForAudit() is an initial audit check that will pass response to handler */
  checkForAudit(getAuditParams: any) {
    this.awaitingAuditsCall = true;
    this.checkForAudits
      .getAudits(getAuditParams)
      .pipe(takeUntil(this._onDestroy))
      .subscribe(response => {
        this.handleCheckAuditResponse(response);
      });
  }

  /**
   * handleAuditresponse() will check if pending audit is found, set
   * pending audit banner and prevent submitting the changes
   * @param response is the checkForAudit() response
   */
  handleCheckAuditResponse(response: any) {
    if (response.results?.length) this.setAuditPending();
    this.awaitingAuditsCall = false;
  }

  /* createAudit() will attempt to create an audit and call an appropriate response handler */
  createAudit() {
    this.awaitingAuditsCall = true;
    const createAuditParams: ICreateAuditParams = {
      SystemID: this.configData.documentInfo.SystemID,
      AuditTypeID: 1,
      Changes: [
        {
          ReferenceID: this.configData.documentInfo.DocumentID,
          Note: this.auditNote,
          Changes: this.configData.changes.map(change => ({
            ReferenceDataID: change.ReferenceDataID,
            DataFieldDefinitionID: change.DataFieldDefinitionID,
            DataValueNew: change.DataValueNew,
          })),
        },
      ],
    };
    this.createAuditService
      .createSingleAudit(createAuditParams)
      .pipe(takeUntil(this._onDestroy))
      .subscribe((createAuditsResponse: any) => {
        this.handleCreateAuditResponse(createAuditsResponse);
        this.awaitingAuditsCall = false;
      });
  }

  /**
   * handleCreateAuditsResponse() will process the createAudit() second response
   * and show pending audit red banner or audit created successfuly green notification
   * @param createAuditRes is the server response for create audit request
   */
  handleCreateAuditResponse(createAuditRes: any): void {
    /* Error response should show a default 'whoops', then close modal */
    if (createAuditRes instanceof HttpErrorResponse) return this.closeModal();

    const extractCreateAuditDbResponse = this.createAuditService.extractCreateAuditDbResponse.bind(
      this.createAuditService,
    );
    const DbErrorLogPendingAuditMessage = this.createAuditService.DbErrorLogPendingAuditMessage;

    const { AuditID, ErrorMessages } = extractCreateAuditDbResponse(createAuditRes);
    if (typeof ErrorMessages === 'string' && ErrorMessages.includes(DbErrorLogPendingAuditMessage))
      return this.setAuditPending();
    if (typeof AuditID === 'number') {
      this.modalResponse.next(this.auditCreatedMessage);
      return this.closeModal();
    }
    /* TODO: Edit document error handling */
    this.closeModal();
  }

  /* setAuditPending() will show red banner on modal and make the Edit doc to do the same */
  setAuditPending() {
    this.auditPending = true;
    this.modalResponse.next(this.auditPendingMessage);
  }

  /**
   * closeModal() will close the modal and pass value to subscriber
   * @param value is a boolean value to be emitted
   */
  closeModal() {
    this.modalService.hideModalById(this.configData.modalID);
  }

  ngOnInit(): void {
    this.checkForAudit({
      SystemID: this.configData.documentInfo.SystemID,
      DocumentTypeID: this.configData.documentInfo.DocumentTypeID,
      DocumentID: this.configData.documentInfo.DocumentID,
      AuditApprovalStatusID: 1,
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
    this.modalResponse.complete();
  }
}
